import { window } from "global";
import { useEffect } from "react";
import { useGlobal } from "reactn";
import { MediaQuery } from "@outplayed/responsive";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { TierListHeader } from "@ugg/shared/components/TierList/TierListHeader";
import { TierListPageNav } from "@ugg/shared/components/TierList/TierListPageNav";
import { TierListFilterManager } from "@ugg/shared/components/TierList/TierListFilterManager";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";
import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { ChampionLink } from "@ugg/shared/components/TierList/common/ChampionLink";
import { QueueTypeC, QueueTypeN } from "@ugg/shared/utils/queue-type-helpers";

type BannerProp = {
  bannerSlot?: JSX.Element | null;
  patchNotesCTA?: React.ReactNode;
};

//----------

// TIER_ORDER is the single source of truth for valid tier names —
// both the type and the render loop derive from it. `as const`
// widens it to a literal-tuple type so TierName below resolves to
// "S+" | "S" | "A" | "B" | "C" | "D" rather than string.
const TIER_ORDER = ["S+", "S", "A", "B", "C", "D"] as const;
type ChampionName = string;
type TierNameValue = "S+" | "S" | "A" | "B" | "C" | "D";
type AugmentRarity = "kPrismatic" | "kGold" | "kSilver";

export interface AugmentTierList {
  rarities: Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>;
  lastUpdated: string;
}

export interface AugmentAggregatedTierList {
  tiers: Record<TierNameValue, number[]>; // integer augment IDs
  lastUpdated: string;
}

// Manifest shape — keyed by stringified augment id (JSON object keys
// have to be strings; the integer id is also carried inside each
// entry for code that needs to iterate by number). The manifest is
// the canonical source of truth for augment metadata across the page:
// the tier lists carry just ids, and consumers cross-reference here
// to get icons + display names. `rarity` is left as `string` rather
// than the narrow AugmentRarity union because the upstream
// cherry-augments.json carries values outside the three tier-list
// rarities (kEventChoice, kBronze) — the manifest preserves them
// even though the tier-list pages only show three.
export interface AugmentManifestEntry {
  id: number;
  augmentNameId: string;
  nameTRA: string;
  rarity: string;
  iconUrl: string;
}

export type AugmentManifest = Record<string, AugmentManifestEntry>;

// Type matches the JSON shape — object with two named keys, not a
// tuple. Records constrain to the 6 valid tier letters so
// `data.tiers.foo` would be a compile-time error.
export interface AramMayhemChampionTierList {
  tiers: Record<TierNameValue, ChampionName[]>;
  lastUpdated: string;
}

//----------

// cleanAramMayhemChampionTierList pulls fields off `data` instead of returning stubs.
// Optional-chaining handles the `data === null | undefined` case
// the fetcher might pass during loading; nullish-coalescing seeds
// defaults.
export function cleanAramMayhemChampionTierList(data: AramMayhemChampionTierList | null | undefined) {
  const tierlist = data?.tiers ?? ({} as Record<TierNameValue, ChampionName[]>);
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

export function useAramMayhemChampionTierList<K = ReturnType<typeof cleanAramMayhemChampionTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AramMayhemChampionTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-champions-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AramMayhemChampionTierList, K>(url, {
      skip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AramMayhemChampionTierList, ReturnType<typeof cleanAramMayhemChampionTierList>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemChampionTierList(json),
  });
}

//----------

// cleanAramMayhemAugmentTierList — same shape as the champion clean
// helper (defaults to empty record + empty string during loading) but
// the tier values are number arrays, not string arrays. Lives next to
// its hook for the same reason the champion pair does: they're a
// fetch-and-shape unit.
export function cleanAramMayhemAugmentTierList(data: AugmentAggregatedTierList | null | undefined) {
  const tierlist = data?.tiers ?? ({} as Record<TierNameValue, number[]>);
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

// Fetches the aggregated (global, non-rarity-grouped) augment tier
// list — all augments compete in one EMR distribution regardless of
// rarity. Companion to the by-rarity hook below; either can be used
// independently. URL pattern mirrors the champion endpoint, swapping
// `tierlist-champions` for `tierlist-augments`.
export function useAramMayhemAugmentTierList<K = ReturnType<typeof cleanAramMayhemAugmentTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AugmentAggregatedTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-augments-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AugmentAggregatedTierList, K>(url, {
      skip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AugmentAggregatedTierList, ReturnType<typeof cleanAramMayhemAugmentTierList>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemAugmentTierList(json),
  });
}

//----------

// cleanAramMayhemAugmentByRarityTierList — peels off the top-level
// `rarities` object instead of `tiers` (the by-rarity JSON nests the
// tier dict one level deeper, under each rarity). Same loading-state
// fallback pattern as the other clean helpers.
export function cleanAramMayhemAugmentByRarityTierList(data: AugmentTierList | null | undefined) {
  const rarities = data?.rarities ?? ({} as Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>);
  const lastUpdated = data?.lastUpdated ?? "";
  return { rarities, lastUpdated };
}

// Fetches the by-rarity augment tier list — same data source as the
// aggregated hook above, but with three independent EMR distributions
// (one per rarity) instead of one global pool. The component renders
// these as three stacked sections.
export function useAramMayhemAugmentByRarityTierList<K = ReturnType<typeof cleanAramMayhemAugmentByRarityTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AugmentTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-augments-by-rarity-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AugmentTierList, K>(url, {
      skip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AugmentTierList, ReturnType<typeof cleanAramMayhemAugmentByRarityTierList>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemAugmentByRarityTierList(json),
  });
}

//----------

// cleanAramMayhemAugmentManifest — simpler than the tier-list cleaners
// because the manifest is just a flat id→entry dict (no `tiers` /
// `rarities` wrapper, no `lastUpdated`). The empty-object fallback
// keeps the consumer's `manifest[id]` lookup safe during the loading
// state without forcing every callsite to null-check the dict itself.
export function cleanAramMayhemAugmentManifest(data: AugmentManifest | null | undefined) {
  return data ?? ({} as AugmentManifest);
}

// Fetches the augment manifest — one per patch. Used by the augment
// tier-list tables to render real icons + names instead of placeholder
// content. URL pattern mirrors the tier-list endpoints (same patch
// substitution on both sides), pointing at
// `/custom-aram-mayhem/<patch>/aram-mayhem-augment-manifest-<patch>.json`
// where the manifest is uploaded by the AWS step.
export function useAramMayhemAugmentManifest<K = ReturnType<typeof cleanAramMayhemAugmentManifest>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AugmentManifest, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/aram-mayhem-augment-manifest-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AugmentManifest, K>(url, {
      skip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AugmentManifest, ReturnType<typeof cleanAramMayhemAugmentManifest>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemAugmentManifest(json),
  });
}

// ─────────────────────────────────────────────────────────────────────
// Styling helpers
// ─────────────────────────────────────────────────────────────────────

// Per-tier solid background class. Used in three places per card:
// the 4px left border (via the matching `border-[#X]` variant in
// TIER_BANNER_CLASSES), the 1px horizontal divider between banner
// and body, and the small vertical accent bar that sits just left
// of the tier letter inside the banner. Kept as a const map (rather
// than computed) so Tailwind's content scanner picks up the literal
// class strings at build time.
const TIER_LABEL_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#FF9B00]",
  S: "bg-[#3273FA]",
  A: "bg-[#7EA4F4]",
  B: "bg-[#CDDCFE]",
  C: "bg-[#FCB1B2]",
  D: "bg-[#FF4E50]",
};

// Banner styling per tier — combines the border color (applied as
// 4px left border on banner and body, also as the matching divider
// 1px line color via TIER_LABEL_BG) with the gradient start color.
// Full class strings (not template-string interpolations) so
// Tailwind's JIT content scanner picks them up at build time.
//
// The gradient is `bg-gradient-to-r from-[#X]/<opacity> to-transparent`,
// applied to a banner that already has `bg-[#191937]` underneath —
// the tier color fades horizontally from 30% opacity at the left
// to fully transparent at the right, revealing the dark banner
// base. Opacity is 30% for every tier except B, where /20 is used
// because the much lighter #CDDCFE would otherwise overpower the
// gradient.
const TIER_BANNER_CLASSES: Record<TierNameValue, string> = {
  "S+": "border-[#FF9B00] from-[#FF9B00]/30",
  S: "border-[#3273FA] from-[#3273FA]/30",
  A: "border-[#7EA4F4] from-[#7EA4F4]/30",
  B: "border-[#CDDCFE] from-[#CDDCFE]/20",
  C: "border-[#FCB1B2] from-[#FCB1B2]/30",
  D: "border-[#FF4E50] from-[#FF4E50]/30",
};

// Body background color per tier — alternates between the lighter
// (#191937) and darker (#11112A) navy shades to zebra-stripe the
// stacked tier cards. Matches the Figma spec exactly: S+/A/C use
// the lighter shade, S/B/D use the darker. The lighter color also
// matches the banner background, so for the "lighter" tiers
// (S+/A/C) the banner and body share a single background and the
// 1px tier-color divider is the only visual separator between
// them; for the "darker" tiers (S/B/D) the banner sits visibly
// brighter than the body.
const TIER_BODY_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#191937]",
  S: "bg-[#11112A]",
  A: "bg-[#191937]",
  B: "bg-[#11112A]",
  C: "bg-[#191937]",
  D: "bg-[#11112A]",
};

// Iteration order for the by-rarity augment table — Prismatic → Gold
// → Silver, matching the canonical RARITY_DISPLAY_ORDER on the Python
// side (best-to-worst). Explicit constant (rather than Object.keys()
// over the fetched data) so the React order stays stable even if
// upstream JSON key ordering ever changes.
const RARITY_ORDER = ["kPrismatic", "kGold", "kSilver"] as const satisfies readonly AugmentRarity[];

// Human-readable rarity labels for section headers. Decoupled from
// the JSON's `k*` enum keys so localization / display tweaks live
// here instead of in every render site.
const RARITY_DISPLAY: Record<AugmentRarity, string> = {
  kPrismatic: "Prismatic",
  kGold: "Gold",
  kSilver: "Silver",
};

// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemChampionTierListTableProps {
  tierlist: Record<TierNameValue, ChampionName[]>;
}

export function AramMayhemChampionTierListTable(props: AramMayhemChampionTierListTableProps) {
  const { tierlist } = props || {};
  const { getChampionIdByName } = getRiotAssetsContext();
  const convertedQueueType = QueueTypeC.convertToString(QueueTypeN.ARAM) as string;

  return (
    // Vertical stack of tier cards with 24px gaps between them
    // (matches the Figma spec — bumped from gap-3 in the previous
    // iteration to gap-6 to match the reference more closely).
    <div className="flex flex-col gap-6">
      {TIER_ORDER.map((tier) => {
        const champions = tierlist[tier];
        // Skip empty tiers — no "Empty S+" rows. Comment this guard
        // out if you want all 6 tier rows visible regardless.
        if (!champions || champions.length === 0) return null;
        return (
          <section
            key={tier}
            aria-label={`Tier ${tier}`}
            // Card shell: 3px rounded corners. overflow-hidden so
            // the banner's top corners and the body's bottom
            // corners clip cleanly to the rounded outer shape.
            // Note: the 4px left border is applied to the banner
            // and body individually (matching Figma's structure)
            // rather than to this section — that way the 1px
            // horizontal divider can span the full card width
            // including the left-border column, giving a
            // continuous colored "L+T" frame on the left and top
            // of the body.
            className="overflow-hidden rounded-[3px]"
          >
            {/* Banner — fixed 56px height (h-14), 4px left border
                in tier color, dark base background (#191937 — a
                shade lighter than the body to subtly differentiate
                the two regions), and a horizontal gradient that
                fades the tier color from 30% opacity at the left
                to transparent at the right. The h-14 + flex
                items-center keeps the label vertically centered. */}
            <div
              className={`flex h-14 items-center gap-3 border-l-4 bg-[#191937] bg-gradient-to-r to-transparent px-[18px] ${TIER_BANNER_CLASSES[tier]}`}
            >
              <span className={`block h-5 w-0.5 rounded-sm ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
              <h2 className="text-sm font-bold text-white">{tier}</h2>
            </div>
            {/* 1px horizontal divider in the tier color, spanning
                the full card width (no left border on this div, so
                the colored line extends seamlessly over the area
                where the banner / body left borders sit). */}
            <div className={`h-px ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
            {/* Body — per-tier alternating background (lighter
                #191937 for S+/A/C, darker #11112A for S/B/D —
                gives the stacked tier cards a subtle zebra-stripe
                effect from the Figma spec). 4px left border
                continues the column from the banner. Champion
                grid scales 1 / 2 / 3 / 4 / 5 columns across
                breakpoints.

                The `className` override on <ChampionLink> uses a
                Tailwind arbitrary descendant variant to reach the
                inner <strong class="champion-name"> and force a
                14px font size, matching Figma's section-item
                spec. The shared CSS class otherwise locks the
                size at 13px; the `!` prefix makes the utility
                !important so it wins regardless of stylesheet
                load order. */}
            <div
              className={`grid grid-cols-1 gap-x-5 gap-y-5 border-l-4 p-[18px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${TIER_BODY_BG[tier]} ${TIER_BANNER_CLASSES[tier]}`}
            >
              {champions.map((championName) => (
                <ChampionLink
                  key={championName}
                  championId={getChampionIdByName(championName)}
                  queueType={convertedQueueType}
                  dim={36}
                  className="[&_.champion-name]:!text-sm [&>div]:!px-0"
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Augment tier rows — private inner helper shared by both augment
// tables. Renders one set of tier rows from a {tier: [augment_id]}
// map. Lifted out so AramMayhemAugmentTierListTable (one set of rows,
// flat) and AramMayhemAugmentByRarityTierListTable (one set per
// rarity, three sections) don't duplicate the same render logic.
//
// Visual layout mirrors the champion table — same tier-row card,
// same colored left bar, same responsive grid — so the augment and
// champion sections feel visually consistent when stacked on the
// same page. The per-cell content is a placeholder until the augment
// asset pipeline lands: a dashed-border square stands in for the
// future icon, with the augment ID rendered as text beside it.
// Swap the inner card contents (icon img, localized name, hover
// behavior) without touching this surrounding structure.
// ─────────────────────────────────────────────────────────────────────

interface AugmentTierRowsProps {
  tierlist: Record<TierNameValue, number[]>;
  manifest: AugmentManifest;
}

function AugmentTierRows(props: AugmentTierRowsProps) {
  const { tierlist, manifest } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {TIER_ORDER.map((tier) => {
        const augmentIds = tierlist[tier];
        // Skip empty tiers — matches the champion table's behavior.
        if (!augmentIds || augmentIds.length === 0) return null;
        return (
          <section key={tier} aria-label={`Tier ${tier}`} className="overflow-hidden rounded-[3px]">
            {/* Banner — same structure as the champion table:
                h-14, 4px left border, dark base bg with horizontal
                tier-color gradient, small vertical accent bar to
                the left of a 14px bold white tier letter. */}
            <div
              className={`flex h-14 items-center gap-3 border-l-4 bg-[#191937] bg-gradient-to-r to-transparent px-[18px] ${TIER_BANNER_CLASSES[tier]}`}
            >
              <span className={`block h-5 w-0.5 rounded-sm ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
              <h2 className="text-sm font-bold text-white">{tier}</h2>
            </div>
            <div className={`h-px ${TIER_LABEL_BG[tier]}`} aria-hidden="true" />
            {/* Augment grid — same column breakpoints + left border
                + dark background as the champion body. */}
            <div
              className={`grid grid-cols-1 gap-x-5 gap-y-5 border-l-4 p-[18px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${TIER_BODY_BG[tier]} ${TIER_BANNER_CLASSES[tier]}`}
            >
              {augmentIds.map((augmentId) => {
                // Manifest is keyed by stringified id (JSON object keys
                // must be strings). Look up here once per cell.
                const entry = manifest[String(augmentId)];
                if (!entry) {
                  // Fallback for ids not present in the manifest —
                  // shouldn't happen with a fresh manifest fetched on
                  // the same patch, but possible if the tier-list and
                  // manifest get out of sync, or if the manifest is
                  // still loading and somehow rendered early. The
                  // dashed-square + #id form is the same placeholder
                  // we had before the manifest existed, so it's
                  // visually obvious when this branch fires.
                  // Sized to match the real entry (h-9 / 36px) so
                  // layout doesn't shift when entries become available.
                  return (
                    <div key={augmentId} className="flex items-center gap-2">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-dashed border-[#1c2030] bg-[#1a1d2e] font-mono text-[10px] text-[#64748b]">
                        ?
                      </div>
                      <span className="font-mono text-sm text-[#cddcea]">#{augmentId}</span>
                    </div>
                  );
                }
                return (
                  // Real cell — 36×36 icon + name in 14px Barlow
                  // semibold white, matching the Figma item spec.
                  // Icon uses 3px rounded corners (rounded-[3px])
                  // to match Figma's `border-radius: 3px` on the
                  // image mask. lazy loading keeps offscreen tier
                  // rows from hitting the network until the user
                  // scrolls down.
                  <div key={augmentId} className="flex items-center gap-2">
                    <img src={entry.iconUrl} alt={entry.nameTRA} loading="lazy" className="h-9 w-9 shrink-0 rounded-[3px]" />
                    <span className="font-[Barlow] text-sm font-semibold text-white">{entry.nameTRA}</span>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Aggregated augment table — one global tier list across all
// rarities. Thin wrapper around AugmentTierRows; the wrapping exists
// for API symmetry with the champion table (both are exported as
// "...TierListTable") and for a clear extension point if the
// aggregated view eventually grows page-level chrome (filters,
// sort toggles, etc.).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentTierListTableProps {
  tierlist: Record<TierNameValue, number[]>;
  manifest: AugmentManifest;
}

export function AramMayhemAugmentTierListTable(props: AramMayhemAugmentTierListTableProps) {
  const { tierlist, manifest } = props || {};
  return <AugmentTierRows tierlist={tierlist} manifest={manifest} />;
}

// ─────────────────────────────────────────────────────────────────────
// By-rarity augment table — three sections (Prismatic / Gold /
// Silver), each containing an independent tier grouping. Iterates
// in the canonical best-to-worst order from RARITY_ORDER. A section
// is hidden entirely if its `tiers` payload is missing (defensive
// — the JSON always carries all three rarities, but a partial
// fetch or future schema variation shouldn't crash the page).
// ─────────────────────────────────────────────────────────────────────

interface AramMayhemAugmentByRarityTierListTableProps {
  rarities: Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>;
  manifest: AugmentManifest;
}

export function AramMayhemAugmentByRarityTierListTable(props: AramMayhemAugmentByRarityTierListTableProps) {
  const { rarities, manifest } = props || {};

  return (
    // Larger gap between rarity sections than between tier rows —
    // helps visually separate the three independent groupings.
    <div className="flex flex-col gap-6">
      {RARITY_ORDER.map((rarity) => {
        const rarityTiers = rarities?.[rarity]?.tiers;
        if (!rarityTiers) return null;
        return (
          <section key={rarity} className="flex flex-col gap-2">
            {/* Rarity header — plain heading for now. Easy place
                to layer in color coding (e.g. orange for Prismatic,
                gold for Gold, gray for Silver) once we settle on
                the visual treatment. */}
            <h3 className="text-lg font-bold text-[#e2e8f0]">{RARITY_DISPLAY[rarity]}</h3>
            <AugmentTierRows tierlist={rarityTiers} manifest={manifest} />
          </section>
        );
      })}
    </div>
  );
}

export const AramMayhemChampionTierListContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const params = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);
  const { patch } = validatedQueryParams;

  const { data, loading, error } = useAramMayhemChampionTierList({
    params,
  });
  const { tierlist, lastUpdated } = data || {};

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  let table = null;
  if (!window) {
    table = null;
  } else if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = <AramMayhemChampionTierListTable tierlist={tierlist || ({} as Record<TierNameValue, ChampionName[]>)} />;
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" />
      <>{table}</>
    </div>
  );
};

export const AramMayhemAugmentTierListContainer = ({ bannerSlot, patchNotesCTA }: BannerProp) => {
  const params = useQueryString();

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_AUGMENT_TIER_LIST, params, true);
  const { patch } = validatedQueryParams;

  // Augment data — aggregated. Independent fetch from the champion
  // one so a slow augment load doesn't block the champion table from
  // rendering (and vice versa). Variable names are prefixed to avoid
  // shadowing the champion destructures above.
  const { data: augmentData, loading: augmentLoading, error: augmentError } = useAramMayhemAugmentTierList({ params });
  const { tierlist: augmentTierlist, lastUpdated } = augmentData || {};

  // Augment manifest — id → {nameTRA, iconUrl, rarity, ...}. Both
  // augment tables look up against this dict to render real icons +
  // display names. Fetched once and shared (prop-drilled rather than
  // re-fetched per table) so the JSON fetcher's cache isn't strictly
  // necessary for correctness. The data IS just an id→entry dict —
  // no `lastUpdated` envelope — so the cleaner returns the dict
  // directly with an empty-object fallback during loading.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useAramMayhemAugmentManifest({ params });
  const manifest = manifestData || ({} as AugmentManifest);

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Augment table — same loading/error/render pattern as the champion
  // one but driven by the separate augment fetch. SSR guard mirrors
  // the existing pattern (skip render until `window` is defined).
  // The manifest fetch is folded into the augment table's loading +
  // error gating because the table can't render meaningful content
  // without the manifest — defer the table until BOTH are ready,
  // rather than showing one render with placeholder fallback and
  // then a second render once the manifest arrives.
  let augmentTable = null;
  if (!window) {
    augmentTable = null;
  } else if (augmentLoading || manifestLoading) {
    augmentTable = <TierListTablePlaceholder />;
  } else if (augmentError || manifestError) {
    augmentTable = <TierListTableError />;
  } else {
    augmentTable = (
      <AramMayhemAugmentTierListTable tierlist={augmentTierlist || ({} as Record<TierNameValue, number[]>)} manifest={manifest} />
    );
  }

  return (
    <div>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={augmentLoading || manifestLoading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot ?? null}
      <TierListFilterManager className="my-[24px]" />
      <>{augmentTable}</>
    </div>
  );
};
